import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Paper, Switch, Button, Divider, IconButton } from "@mui/material";
import { useState, useEffect, Fragment } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import LsService from "../services/localstorage";
import AddStores from "./AddStores";
import AdminHeaderComp from "../subComponent/AdminHeaderComp";
import AnyModal from "../subComponent/AnyModel";
import EditStores from "./EditStores";
import AddUsers from "./AddUsers";
import { API_URL } from "../services/authService";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ManageUsers from "./ManageUsers";

const ManageStores = () => {
    const [storesList, setStoresList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [openModal, setOpenModal] = useState(false);
    const [selectedStore, setSelectedStore] = useState(null);
    const [openAddUserModal, setOpenAddUserModal] = useState(false);
    const [displayAddStore, setDisplayAddStore] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalStores, setTotalStores] = useState(0);
    const [usersByStore, setUsersByStore] = useState([]); // Store users data by store ID
    const [currentStoreName, setCurrentStoreName] = useState("");
    const [openUserModal, setOpenUserModal] = useState(false);
    const [show, setShow] = useState(false);
    // const [loadingUsers, setLoadingUsers] = useState({});
    // const [displayAddStore, setDisplayAddStore] = useState(false);

    const navigate = useNavigate();

    const headCellStyle = {
        color: "white",
        fontWeight: "bold",
        fontSize: "1rem",
    };

    const fetchStoresData = async (page = 1, rowsPerPage = 10) => {
        try {
            setLoading(true);
            setError("");

            // Get token from localStorage
            const userData = localStorage.getItem('invCreator');
            if (!userData) {
                throw new Error('Authentication token not found');
            }
            const { token } = JSON.parse(userData);

            // Make API call with token
            const response = await axios.get(
                `${API_URL}/get_all_stores?page=${page}&limit=${rowsPerPage}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            // console.log(response.data);
            if (response.data.stores) {
                // console.log(response.data.stores);
                setStoresList(response.data.stores || []);
                setTotalStores(response.data.totalStores || 0); // Update total stores for pagination
            } else {
                setError(response.data.message || "Failed to fetch stores");
            }
        } catch (error) {
            console.error("Error fetching stores:", error);
            setError(error.response?.data?.message || "An error occurred while fetching stores");
        } finally {
            setLoading(false);
        }
    };

    const fetchUsersByStore = async (storeId) => {
        try {
            // Get token from localStorage
            const userData = localStorage.getItem("invCreator");
            if (!userData) {
                throw new Error("Authentication token not found");
            }
            const { token } = JSON.parse(userData);

            // Make API call to fetch users
            const response = await axios.get(`${API_URL}/get_users_by_store_id/${storeId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });

            // console.log(response.data);
            if (response.data) {
                setUsersByStore(response.data);
            } else {
                setError("Failed to fetch users");
            }
        } catch (error) {
            console.error("Error fetching users:", error);
            setError(error.response?.data?.message || "An error occurred while fetching users");
        }
    };

    const toggleStoreStatus = async (storeId, currentStatus) => {
        try {
            // Get token from localStorage
            const userData = localStorage.getItem('invCreator');
            if (!userData) {
                throw new Error('Authentication token not found');
            }
            const { token } = JSON.parse(userData);

            // Find the store in the current list to get all required fields
            const storeToUpdate = storesList.find(store => store.store_id === storeId);
            if (!storeToUpdate) {
                throw new Error('Store not found');
            }

            // Make API call to update store status with all required fields
            const response = await axios.put(
                `${API_URL}/update_store_by_id/${storeId}`,
                {
                    is_active: !currentStatus
                },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );
            if (response.statusText === "OK") {
                // Refresh stores list
                fetchStoresData();
            } else {
                setError(response.data.message || "Failed to update store status");
            }
        } catch (error) {
            console.error("Error updating store status:", error);
            setError(error.response?.data?.message || "An error occurred while updating store status");
        }
    };

    useEffect(() => {
        if (!LsService.getItem("invCreator")) {
            navigate("/login-creator");
        } else {
            fetchStoresData(page + 1, rowsPerPage);
        }
    }, [page, rowsPerPage]);

    const handleOpenModal = (store) => {
        setSelectedStore(store);
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setSelectedStore(null);
    };

    const handleOpenAddUserModal = (store) => {
        setSelectedStore(store);
        setOpenAddUserModal(true);
    };

    const handleCloseAddUserModal = () => {
        setOpenAddUserModal(false);
        setSelectedStore(null);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0); // Reset to the first page
    };

    const onDisplaySuccess = () => {
        alert("User added successfully");
    }

    const toggleUsersVisibility = (storeId, storeName) => {
        setShow(true);
        setCurrentStoreName(storeName);
        fetchUsersByStore(storeId);
        setOpenUserModal(true);
    };

    const handleCloseUserModal = () => {
        setOpenUserModal(false);
        setShow(false);
        setUsersByStore([]); // Reset usersByStore when closing the modal
    };

    return (
        <Box sx={{
            background: "black",
            minHeight: "100vh",
        }}>
            <AdminHeaderComp />
            <Box sx={{
                width: { xs: "100vw", md: "98.93vw" },
                minHeight: "calc(100vh - 3.5rem)",
                backgroundColor: "#0f172a",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: { md: "center", xs: "start" },
                color: "white",
                py: 2,
            }}
            >
                {displayAddStore ?
                    <AddStores fetchStoresData={fetchStoresData} setDisplayAddStore={setDisplayAddStore} />
                    : <Button
                        variant="contained" color="success"
                        onClick={() => setDisplayAddStore(true)} >Add Store</Button>
                }
                <Box p={1} />
                {error && (
                    <Typography color="error" sx={{ textAlign: "center", mb: 2 }}>
                        {error}
                    </Typography>
                )}
                <TableContainer component={Paper} sx={{ maxWidth: { xs: "100%", md: "80%" }, mx: "auto" }}>
                    <Table>
                        <TableHead sx={{ backgroundColor: "#3e8596" }}>
                            <TableRow>
                                <TableCell sx={headCellStyle}>Store Name</TableCell>
                                <TableCell sx={headCellStyle}>Address</TableCell>
                                <TableCell sx={headCellStyle}>Phone</TableCell>
                                <TableCell sx={headCellStyle}>Email</TableCell>
                                <TableCell sx={headCellStyle}>Add Users</TableCell>
                                <TableCell sx={headCellStyle}>Action</TableCell>
                                <TableCell sx={headCellStyle}>Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">
                                        Loading...
                                    </TableCell>
                                </TableRow>
                            ) : storesList.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">
                                        No stores found
                                    </TableCell>
                                </TableRow>
                            ) : (
                                storesList.map((store, index) => (
                                    <TableRow
                                        sx={{
                                            backgroundColor: index % 2 === 0 ? "white" : "lightgrey",
                                            "&:hover td": {
                                                color: "green",
                                            },
                                        }}
                                        key={store.store_id}
                                    >
                                        <TableCell>
                                            {store.name}
                                            <IconButton
                                                onClick={() => toggleUsersVisibility(store.store_id, store.name)}
                                                edge="end"
                                            >
                                                {show ? (
                                                    <VisibilityOffIcon />
                                                ) : (
                                                    <VisibilityIcon />
                                                )}
                                            </IconButton>
                                        </TableCell>
                                        <TableCell>{store.address}</TableCell>
                                        <TableCell>{store.phone}</TableCell>
                                        <TableCell>{store.email}</TableCell>
                                        <TableCell>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                onClick={() => handleOpenAddUserModal(store)}
                                            >
                                                Add
                                            </Button>
                                        </TableCell>
                                        <TableCell>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                onClick={() => handleOpenModal(store)}
                                            >
                                                Edit
                                            </Button>
                                        </TableCell>
                                        <TableCell>
                                            <Switch
                                                checked={store.is_active}
                                                onChange={() =>
                                                    toggleStoreStatus(store.store_id, store.is_active)
                                                }
                                                color="primary"
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Divider />
                <TablePagination
                    sx={{ backgroundColor: "white", width: "80%", }}
                    component="div"
                    count={totalStores}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Box>

            <AnyModal
                open={openModal}
                onClose={handleCloseModal}
                headTitle="Edit Store Data"
                ComponentComp={EditStores}
                data={selectedStore}
                fetch={fetchStoresData}
            />

            {/* New Add User Modal */}
            <AnyModal
                open={openAddUserModal}
                onClose={handleCloseAddUserModal}
                headTitle="Add Admin/Casher"
                ComponentComp={selectedStore ? AddUsers : Box}
                data={selectedStore}
                fetch={onDisplaySuccess}
            />

            {/* New display User Modal */}
            <AnyModal
                open={openUserModal}
                onClose={handleCloseUserModal}
                headTitle={`Users of Store: ${currentStoreName}`}
                ComponentComp={usersByStore ? ManageUsers : Box}
                data={usersByStore}
                // fetch={onDisplaySuccess}
            />
        </Box>
    );
};

export default ManageStores;